import React, { useRef, useState } from 'react';
import templateImg from './badge_template.jpeg';
import styles from './Badge.module.css';

/* Fit text within its box, scaling with the card width (cqw) so the on-screen
   badge matches the downloaded PNG. `maxCqw`/`minCqw` are container-width units. */
const FitText = ({ text, className, maxCqw = 5, minCqw = 2, lines = 1 }) => {
  const ref = useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = el.closest('[data-badge-frame]') || el.parentElement;
    const cw = container ? container.clientWidth : 200;
    let s = maxCqw;
    el.style.fontSize = (s / 100 * cw) + 'px';
    let guard = 200;
    while (s > minCqw && guard-- > 0 && (el.scrollWidth > el.clientWidth + 0.5 || el.scrollHeight > el.clientHeight + 0.5)) {
      s -= 0.1;
      el.style.fontSize = (s / 100 * cw) + 'px';
    }
  }, [text, maxCqw, minCqw, lines]);
  return <span ref={ref} className={className}>{text}</span>;
};

/**
 * ChapterBadge — uses the badge_template.jpeg artwork with the student's
 * name + chapter title overlaid. Everything else (Congratulations, medal,
 * ribbons, PrrepX footer) comes from the image, so it's a 1:1 match.
 */
const Badge = ({
  title = '',
  subtitle = '',
  studentName = '',
  size = 'md',
  downloadable = true,
}) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Draw the badge to a canvas: template image + name & chapter at measured spots.
  const handleDownload = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const img = new Image();
      img.src = templateImg;
      await img.decode();

      const W = img.naturalWidth, H = img.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, W, H);

      const name = (studentName || 'Student').toUpperCase();
      const chapter = title.replace(/^chapter\s*\d+\s*[·:\-–—]?\s*/i, '');

      // shrink-to-fit helper
      const fitFont = (text, weight, maxPx, maxWidth) => {
        let s = maxPx;
        ctx.font = `${weight} ${s}px Inter, Arial, sans-serif`;
        while (s > 6 && ctx.measureText(text).width > maxWidth) { s -= 0.5; ctx.font = `${weight} ${s}px Inter, Arial, sans-serif`; }
        return s;
      };

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#c2410c';

      // Name on gold banner (~19% from top), width ~66% of image
      ctx.textBaseline = 'middle';
      fitFont(name, 900, H * 0.022, W * 0.62);
      ctx.fillText(name, W / 2, H * 0.19 + (H * 0.021));

      // Chapter on plate (~70% from top) — use the full plate width, wrap to 2 lines
      ctx.fillStyle = '#c2410c';
      const chMaxW = W * 0.78;             // full empty plate width
      const plateY = H * 0.704;
      const wrap2 = (text, maxW) => {
        const words = text.split(' ');
        let l1 = '', l2 = '';
        words.forEach(w => {
          if (ctx.measureText((l1 + ' ' + w).trim()).width <= maxW) l1 = (l1 + ' ' + w).trim();
          else l2 = (l2 + ' ' + w).trim();
        });
        return [l1, l2];
      };
      // Start at plate-fitting size; shrink until it fits on ONE line, or if it must
      // wrap, until BOTH wrapped lines fit the width (smaller text when 2 lines).
      let cs = H * 0.020;
      ctx.font = `900 ${cs}px Inter, Arial, sans-serif`;
      let lines = ctx.measureText(chapter).width <= chMaxW ? [chapter, ''] : wrap2(chapter, chMaxW);
      while (cs > 8 && (ctx.measureText(lines[0]).width > chMaxW || (lines[1] && ctx.measureText(lines[1]).width > chMaxW))) {
        cs -= 0.5;
        ctx.font = `900 ${cs}px Inter, Arial, sans-serif`;
        lines = ctx.measureText(chapter).width <= chMaxW ? [chapter, ''] : wrap2(chapter, chMaxW);
      }
      if (lines[1]) {
        const lh = cs * 1.15;
        ctx.fillText(lines[0], W / 2, plateY - lh / 2);
        ctx.fillText(lines[1], W / 2, plateY + lh / 2);
      } else {
        ctx.fillText(lines[0], W / 2, plateY);
      }

      const link = document.createElement('a');
      const safeTitle = (chapter || 'badge').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      link.download = `PrrepX-Chapter-Badge-${safeTitle}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) { console.error('Badge download failed', err); } finally { setBusy(false); }
  };

  return (
    <div className={`${styles.card} ${size === 'lg' ? styles.cardLg : ''}`}>
      <div className={styles.frame} ref={cardRef} data-badge-frame>
        <img className={styles.bgImg} src={templateImg} alt="Chapter badge" />

        {/* user name on the gold banner */}
        <div className={styles.nameOverlay}>
          <FitText text={(studentName || 'Student').toUpperCase()} className={styles.nameText} maxCqw={4.6} minCqw={2.4} />
        </div>

        {/* chapter name on the plate — strip the "Chapter N ·" prefix (the plate already says "Chapter") */}
        <div className={styles.chapterOverlay}>
          <FitText text={title.replace(/^chapter\s*\d+\s*[·:\-–—]?\s*/i, '')} className={styles.chapterText} maxCqw={4.2} minCqw={2.2} lines={2} />
        </div>
      </div>

      {downloadable && (
        <button type="button" className={styles.dlBtn} onClick={handleDownload} disabled={busy} title="Download badge as PNG">
          {busy ? '…' : '⬇ PNG'}
        </button>
      )}
    </div>
  );
};

export default Badge;
